import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNotification } from '../contexts/NotificationContext'
import { compareDivisions } from '../lib/divisionOrder'
import {
  deleteBracketTeams,
  deleteSeeds,
  fetchBracketWithDetails,
  fetchTournamentBrackets,
  saveBracketData,
} from '../services/bracketService'

export default function useBracketPopulation(bracketId) {
  const notify = useNotification()
  const [bracket, setBracket] = useState(null)
  const [tournament, setTournament] = useState(null)
  const [tournamentBrackets, setTournamentBrackets] = useState([])
  const [loading, setLoading] = useState(true)
  const [newMatchId, setNewMatchId] = useState(null)
  const matchRefs = useRef({})
  const lastScrolledMatchIdRef = useRef(null)
  const stickyHeaderRef = useRef(null)
  const [isHeaderSticky, setIsHeaderSticky] = useState(false)
  const [saving, setSaving] = useState(false)

  // Matches state - array of match objects
  const [matches, setMatches] = useState([])
  // Track original matches and players from database for deletion detection
  const [originalMatches, setOriginalMatches] = useState([])
  const [originalPlayers, setOriginalPlayers] = useState([])
  // Validation errors
  const [validationErrors, setValidationErrors] = useState({})
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [touchedFields, setTouchedFields] = useState(() => new Set())

  useEffect(() => {
    if (bracketId) {
      fetchBracket()
    }
  }, [bracketId])

  // Auto-focus when a new match is created
  useEffect(() => {
    if (newMatchId) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        // Find the new match
        const newMatch = matches.find(m => m.id === newMatchId)
        if (newMatch) {
          if (newMatch.round === 0) {
            // For PQ matches, focus the first seed field
            const seedSelector = `[data-validation-key="match-${newMatch.id}-p1-seed"]`
            const seedInput = document.querySelector(seedSelector)
            if (seedInput) {
              seedInput.focus()
            }
          } else {
            // For regular matches, focus the first PQ button
            const pqButtonSelector = `[data-parent-pq-button="${1000 + (newMatch.matchNumber * 10) + 1}"]`
            const pqButton = document.querySelector(pqButtonSelector)
            if (pqButton) {
              pqButton.focus()
              pqButton.setAttribute('data-force-focus', 'true')
              pqButton.addEventListener(
                'blur',
                () => {
                  pqButton.removeAttribute('data-force-focus')
                },
                { once: true }
              )
            }
          }
        }
        setNewMatchId(null)
      }, 100)
    }
  }, [newMatchId, matches])

  const handleFocusCapture = useCallback((e) => {
    const target = e.target
    if (!target || typeof target.closest !== 'function') return

    const container = target.closest('[data-match-container="true"]')
    if (!container) return

    const matchId = container.getAttribute('data-match-id')
    if (matchId && lastScrolledMatchIdRef.current === matchId) return
    lastScrolledMatchIdRef.current = matchId

    requestAnimationFrame(() => {
      // Check if container is already visible in viewport
      const rect = container.getBoundingClientRect()
      const isVisible = (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      )

      // Only scroll if container is not fully visible
      if (!isVisible) {
        container.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
      }
    })
  }, [])

  const handleBlurCapture = useCallback((e) => {
    const target = e.target
    if (!target || typeof target.closest !== 'function') return

    const keyedEl = target.closest('[data-validation-key]')
    if (!keyedEl) return

    const key = keyedEl.getAttribute('data-validation-key')
    if (!key) return

    setTouchedFields((prev) => {
      if (prev.has(key)) return prev
      const next = new Set(prev)
      next.add(key)
      return next
    })
  }, [])

  const displayValidationErrors = useMemo(() => {
    if (showValidationErrors) return validationErrors

    const filtered = {}
    Object.entries(validationErrors).forEach(([key, msg]) => {
      if (touchedFields.has(key)) {
        filtered[key] = msg
      }
    })

    // If any "Duplicate seed" error is shown, show ALL duplicate seed errors
    // so the admin can see both conflicting entries
    const hasDuplicateSeedShown = Object.values(filtered).some(msg => msg === 'Duplicate seed')
    if (hasDuplicateSeedShown) {
      Object.entries(validationErrors).forEach(([key, msg]) => {
        if (msg === 'Duplicate seed') {
          filtered[key] = msg
        }
      })
    }

    return filtered
  }, [showValidationErrors, touchedFields, validationErrors])

  // Run validation whenever matches change
  useEffect(() => {
    if (matches.length > 0 && bracket) {
      const errors = {}
      // Flat seed -> count map across the whole bracket. We previously scoped
      // duplicate detection per "side" (computeSideKey), which let seed 1 in
      // R1-M1 coexist with seed 1 in R1-M23 once an inserted match landed on
      // the opposite half. We don't currently model bracket regions on the
      // seed editor, so a 1-region bracket would never want that — flag every
      // duplicate uniformly until region awareness lands. Brackets that
      // legitimately repeat seeds across regions (e.g. NBA Playoffs, March
      // Madness) will be handled when regions become first-class.
      const seedCounts = new Map()

      // First pass: Count seed occurrences across the whole bracket
      matches.forEach((match) => {
        if (!match.player1PQRef) {
          const seed1Str = String(match.player1Seed || '').trim()
          if (seed1Str) {
            const seed1 = parseInt(seed1Str)
            if (!isNaN(seed1) && seed1 >= 1 && seed1 <= 999) {
              seedCounts.set(seed1, (seedCounts.get(seed1) || 0) + 1)
            }
          }
        }
        if (!match.player2PQRef) {
          const seed2Str = String(match.player2Seed || '').trim()
          if (seed2Str) {
            const seed2 = parseInt(seed2Str)
            if (!isNaN(seed2) && seed2 >= 1 && seed2 <= 999) {
              seedCounts.set(seed2, (seedCounts.get(seed2) || 0) + 1)
            }
          }
        }
      })

      // Second pass: Generate errors
      matches.forEach((match) => {
        const matchKey = `match-${match.id}`
        const isTeam = bracket?.divisionTeamSize > 1

        // Validate player1 seed
        if (!match.player1PQRef) {
          const seed1Str = String(match.player1Seed || '').trim()
          if (!seed1Str) {
            errors[`${matchKey}-p1-seed`] = 'Seed required'
          } else {
            const seed1 = parseInt(seed1Str)
            if (isNaN(seed1) || seed1 < 1) {
              errors[`${matchKey}-p1-seed`] = 'Must be 1-999'
            } else if (seed1 > 999) {
              errors[`${matchKey}-p1-seed`] = 'Max 999'
            } else if (seedCounts.get(seed1) > 1) {
              errors[`${matchKey}-p1-seed`] = 'Duplicate seed'
            }
          }

          // Validate player1 field
          if (isTeam) {
            // For teams, require all team member slots to be filled (real player or TBD)
            const teamSize = bracket?.divisionTeamSize || 2
            const hasCompleteTeam =
              Array.isArray(match.player1) &&
              match.player1.length === teamSize &&
              match.player1.every((p) => !!p?.id)

            if (!hasCompleteTeam) {
              errors[`${matchKey}-p1-player`] = 'Player required'
            }
          } else {
            // For singles, require a selection; TBD is a valid selection
            if (!match.player1?.id) {
              errors[`${matchKey}-p1-player`] = 'Player required'
            }
          }
        }

        // Validate player2 seed
        if (!match.player2PQRef) {
          const seed2Str = String(match.player2Seed || '').trim()
          if (!seed2Str) {
            errors[`${matchKey}-p2-seed`] = 'Seed required'
          } else {
            const seed2 = parseInt(seed2Str)
            if (isNaN(seed2) || seed2 < 1) {
              errors[`${matchKey}-p2-seed`] = 'Must be 1-999'
            } else if (seed2 > 999) {
              errors[`${matchKey}-p2-seed`] = 'Max 999'
            } else if (seedCounts.get(seed2) > 1) {
              errors[`${matchKey}-p2-seed`] = 'Duplicate seed'
            }
          }

          // Validate player2 field
          if (isTeam) {
            // For teams, require all team member slots to be filled (real player or TBD)
            const teamSize = bracket?.divisionTeamSize || 2
            const hasCompleteTeam =
              Array.isArray(match.player2) &&
              match.player2.length === teamSize &&
              match.player2.every((p) => !!p?.id)

            if (!hasCompleteTeam) {
              errors[`${matchKey}-p2-player`] = 'Player required'
            }
          } else {
            // For singles, require a selection; TBD is a valid selection
            if (!match.player2?.id) {
              errors[`${matchKey}-p2-player`] = 'Player required'
            }
          }
        }
      })

      setValidationErrors(errors)
    }
  }, [matches, bracket])

  // Helper function to get all used player IDs in the bracket
  const getUsedPlayerIds = () => {
    const playerIds = new Set()
    const isTeam = bracket?.divisionTeamSize > 1

    matches.forEach((match) => {
      // Collect player1 IDs
      if (isTeam && Array.isArray(match.player1)) {
        match.player1.forEach(player => {
          if (player?.id && player.id !== 'TBD') {
            playerIds.add(player.id)
          }
        })
      } else if (match.player1?.id && match.player1.id !== 'TBD') {
        playerIds.add(match.player1.id)
      }

      // Collect player2 IDs
      if (isTeam && Array.isArray(match.player2)) {
        match.player2.forEach(player => {
          if (player?.id && player.id !== 'TBD') {
            playerIds.add(player.id)
          }
        })
      } else if (match.player2?.id && match.player2.id !== 'TBD') {
        playerIds.add(match.player2.id)
      }
    })

    return Array.from(playerIds)
  }

  const fetchBracket = async () => {
    try {
      setLoading(true)

      // Load bracket data using get_bracket_with_full_details
      const data = await fetchBracketWithDetails(bracketId)

      if (data) {
        const divisionInfo = data.division || {}
        const tournamentInfo = data.tournament || {}
        const seeds = data.seeds || []

        // Set bracket info with division data
        setBracket({
          id: data.id,
          tournamentId: data.tournament_id,
          divisionId: data.division_id,
          divisionName: divisionInfo.name,
          divisionGender: divisionInfo.gender,
          divisionTeamSize: divisionInfo.team_size,
          sportId: tournamentInfo.sport_id || null,
          status: data.status,
          format: data.format || 'single_elim',
        })

        // Set tournament info
        setTournament(tournamentInfo)

        // Fetch all brackets for this tournament so admin can switch between them
        let bracketsData
        try {
          bracketsData = await fetchTournamentBrackets(tournamentInfo.id)
        } catch (bracketsFetchErr) {
          console.error('Error fetching tournament brackets:', bracketsFetchErr)
        }

        if (bracketsData) {
          setTournamentBrackets(
            bracketsData.map(b => ({
              id: b.id,
              divisionName: b.divisions?.name || 'Unknown Division',
              divisionGender: b.divisions?.gender,
              divisionTeamSize: b.divisions?.team_size,
            })).sort(compareDivisions)
          )
        }

        // Build a map of bracket_team_id -> team data from seeds
        const teamMap = new Map()
        seeds.forEach(seed => {
          if (seed.team_one) {
            teamMap.set(seed.team_one.id, seed.team_one)
          }
          if (seed.team_two) {
            teamMap.set(seed.team_two.id, seed.team_two)
          }
        })

        // Build maps of PQ matches by their feeds_into target (round, match_number, slot)
        const pqMatchesByTarget = new Map()

        seeds.forEach(seed => {
          if (seed.round === 0 && seed.feeds_into_round && seed.feeds_into_match_number && seed.feeds_into_slot) {
            const key = `${seed.feeds_into_round}-${seed.feeds_into_match_number}-${seed.feeds_into_slot}`
            pqMatchesByTarget.set(key, seed)
          }
        })

        // Helper function to compute next round/match for binary tree structure
        const computeNextMatchLocal = (round, matchNumber) => {
          const nextRound = round + 1
          const nextMatchNumber = Math.ceil(matchNumber / 2)
          return { nextRound, nextMatchNumber }
        }

        // Transform matches for UI
        const transformMatch = (seed) => {
          // Get team data for team 1 and team 2
          const team1 = seed.team_one
          const team2 = seed.team_two

          // Check if this match has PQs feeding into it using feeds_into_* fields
          const player1PQKey = `${seed.round}-${seed.match_number}-1`
          const player2PQKey = `${seed.round}-${seed.match_number}-2`
          const player1PQMatch = pqMatchesByTarget.get(player1PQKey)
          const player2PQMatch = pqMatchesByTarget.get(player2PQKey)

          // Compute next match using helper function
          const computed = computeNextMatchLocal(seed.round, seed.match_number)
          const nextRound = computed.nextRound
          const nextMatchNumber = computed.nextMatchNumber
          const feedsIntoMatchKey = nextRound && nextMatchNumber ? `${nextRound}-${nextMatchNumber}` : null

          // Handle PQ references using match_number from PQ matches
          const player1PQRef = player1PQMatch ? `PQ-${player1PQMatch.match_number}` : null
          const player2PQRef = player2PQMatch ? `PQ-${player2PQMatch.match_number}` : null

          // Convert team to UI format
          const convertTeamToUI = (team) => {
            if (!team) return null

            const isTBD = !team.players || team.players.length === 0
            const tbdPlayer = { id: 'TBD', name: 'TBD', gender: null }

            // If team size > 1, return array of player objects
            if (divisionInfo.team_size > 1) {
              if (isTBD) {
                // Return array of TBD player objects matching team size
                return Array.from({ length: divisionInfo.team_size }, () => ({ ...tbdPlayer }))
              }

              let teamPlayers = [...team.players]

              // For mixed doubles, ensure female is always first, male second
              if (divisionInfo.gender === 'mixed') {
                if (teamPlayers.length === 2) {
                  // Both slots filled - sort by gender
                  teamPlayers.sort((a, b) => {
                    if (a.gender === 'female' && b.gender === 'male') return -1
                    if (a.gender === 'male' && b.gender === 'female') return 1
                    return 0
                  })
                } else if (teamPlayers.length === 1) {
                  // Only one player - put them in correct slot based on gender
                  const player = teamPlayers[0]
                  if (player.gender === 'male') {
                    // Male goes in slot 2 (index 1), female slot (index 0) is TBD
                    teamPlayers = [{ ...tbdPlayer }, player]
                  } else {
                    // Female goes in slot 1 (index 0), male slot (index 1) is TBD
                    teamPlayers = [player, { ...tbdPlayer }]
                  }
                }
              }

              // Ensure we always return the correct team size
              while (teamPlayers.length < divisionInfo.team_size) {
                teamPlayers.push({ ...tbdPlayer })
              }

              return teamPlayers
            }

            // Singles: return single player object or TBD
            if (isTBD) {
              return { ...tbdPlayer }
            }
            return team.players[0]
          }

          return {
            id: seed.id,
            round: seed.round,
            matchNumber: seed.match_number,
            player1: convertTeamToUI(team1),
            player1Seed: team1?.seed || '',
            player2: convertTeamToUI(team2),
            player2Seed: team2?.seed || '',
            player1PQRef: player1PQRef,
            player2PQRef: player2PQRef,
            feedsIntoMatchKey: feedsIntoMatchKey,
            nextRound,
            nextMatchNumber,
            feedsIntoRound: seed.feeds_into_round,
            feedsIntoMatchNumber: seed.feeds_into_match_number,
            feedsIntoSlot: seed.feeds_into_slot
          }
        }

        // The admin builder only authors the play-in round (round 0) and the
        // base round (round 1). Later rounds (quarterfinals, semis, final) are
        // materialized as skeleton seed rows by the DB the moment official
        // results are entered (materialize_single_elim_rounds) purely so
        // advancement/scoring has somewhere to land. Those rows must NOT surface
        // in the seed editor — they'd render as empty "R2M1"/"R3M1" matches at
        // the bottom of the list (DUB bug report: "randomly adds blank rounds").
        // Keep round <= 1 for single_elim; other formats are unaffected (pool
        // play uses PoolPlaySetup and never renders this list).
        const isSingleElim = (data.format || 'single_elim') === 'single_elim'
        const isBuilderRound = (round) => !isSingleElim || round <= 1

        // Reorder matches: insert PQ matches right after the match they feed into
        const transformedMatches = []
        seeds.forEach(seed => {
          // Skip PQ matches in this pass, they'll be inserted after their parent match
          if (seed.round === 0) return

          // Skip materialized advancement rounds (round >= 2) — not editable here
          if (!isBuilderRound(seed.round)) return

          // Add the regular match
          transformedMatches.push(transformMatch(seed))

          // Check for PQs feeding into this match (either slot)
          const player1PQKey = `${seed.round}-${seed.match_number}-1`
          const player2PQKey = `${seed.round}-${seed.match_number}-2`
          const player1PQ = pqMatchesByTarget.get(player1PQKey)
          const player2PQ = pqMatchesByTarget.get(player2PQKey)

          if (player1PQ) {
            transformedMatches.push(transformMatch(player1PQ))
          }
          if (player2PQ) {
            transformedMatches.push(transformMatch(player2PQ))
          }
        })

        setMatches(transformedMatches)
        // Store original matches and teams for deletion tracking
        setOriginalMatches(transformedMatches.filter(m => typeof m.id === 'string')) // Only DB matches have UUID strings
        setOriginalPlayers(Array.from(teamMap.values()))

        // Mark all existing fields as touched so validation errors show immediately
        const initialTouchedFields = new Set()
        transformedMatches.forEach(match => {
          const matchKey = `match-${match.id}`

          // Mark seed fields as touched if they don't have PQ references
          if (!match.player1PQRef) {
            initialTouchedFields.add(`${matchKey}-p1-seed`)
            initialTouchedFields.add(`${matchKey}-p1-player`)
          }
          if (!match.player2PQRef) {
            initialTouchedFields.add(`${matchKey}-p2-seed`)
            initialTouchedFields.add(`${matchKey}-p2-player`)
          }
        })
        setTouchedFields(initialTouchedFields)
      }
    } catch (err) {
      console.error('Error fetching bracket:', err)
    } finally {
      setLoading(false)
    }
  }

  // Ref to hold save function for keyboard shortcut
  const saveRef = useRef(null)

  // Keyboard shortcuts: Ctrl+S / Cmd+S to save
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        // Only save if not loading and we have data
        if (!loading && bracket && tournament && saveRef.current) {
          saveRef.current()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  })

  // Detect when sticky header becomes stuck
  useEffect(() => {
    const headerEl = stickyHeaderRef.current
    if (!headerEl) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHeaderSticky(!entry.isIntersecting)
      },
      { threshold: [1], rootMargin: '-1px 0px 0px 0px' }
    )

    observer.observe(headerEl)
    return () => observer.disconnect()
  }, [])

  // Compute next round/match for binary tree structure
  const computeNextMatch = (currentRound, currentMatchNumber) => {
    const nextRound = currentRound + 1
    const nextMatchNumber = Math.ceil(currentMatchNumber / 2)
    return { nextRound, nextMatchNumber }
  }

  // Renumber all matches sequentially after a deletion to fill gaps.
  // Returns a new array with corrected matchNumbers and all cross-references updated.
  const renumberAllMatches = (matchList) => {
    // Step 1: Build old->new number maps for regular matches (per round)
    const regularRenumberMap = new Map() // "round-oldMatchNumber" -> newMatchNumber
    const matchesByRound = {}
    matchList.filter(m => m.round >= 1).forEach(m => {
      if (!matchesByRound[m.round]) matchesByRound[m.round] = []
      matchesByRound[m.round].push(m)
    })
    Object.entries(matchesByRound).forEach(([round, roundMatches]) => {
      roundMatches.sort((a, b) => a.matchNumber - b.matchNumber)
      roundMatches.forEach((match, index) => {
        regularRenumberMap.set(`${round}-${match.matchNumber}`, index + 1)
      })
    })

    // Build old->new number map for PQ matches.
    // Sort by parent match position (using the renumbered parent number) then
    // by feeds-into slot, so a freshly-added PQ slots into the right place
    // relative to its parent's bracket position rather than always landing
    // at the tail (e.g. inserting an R1 in the middle and adding a PQ to it
    // should not produce PQ-{maxPQ+1} — DUB bug report).
    const pqParentSortKey = (match) => {
      const parentKey = `${match.feedsIntoRound}-${match.feedsIntoMatchNumber}`
      const renumberedParent = regularRenumberMap.get(parentKey) ?? match.feedsIntoMatchNumber ?? 0
      return {
        round: match.feedsIntoRound ?? 0,
        parent: renumberedParent,
        slot: match.feedsIntoSlot ?? 0,
      }
    }
    const pqRenumberMap = new Map() // oldPQMatchNumber -> newPQMatchNumber
    matchList
      .filter(m => m.round === 0)
      .sort((a, b) => {
        const ak = pqParentSortKey(a)
        const bk = pqParentSortKey(b)
        if (ak.round !== bk.round) return ak.round - bk.round
        if (ak.parent !== bk.parent) return ak.parent - bk.parent
        return ak.slot - bk.slot
      })
      .forEach((match, index) => {
        pqRenumberMap.set(match.matchNumber, index + 1)
      })

    // Step 2: Single pass - apply renumbering and update all cross-references
    return matchList.map(match => {
      if (match.round === 0) {
        // PQ match: renumber and update parent references
        const newMatchNumber = pqRenumberMap.get(match.matchNumber) ?? match.matchNumber
        const parentKey = `${match.feedsIntoRound}-${match.feedsIntoMatchNumber}`
        const newParentMatchNumber = regularRenumberMap.get(parentKey) ?? match.feedsIntoMatchNumber
        return {
          ...match,
          matchNumber: newMatchNumber,
          feedsIntoMatchNumber: newParentMatchNumber,
          nextMatchNumber: newParentMatchNumber,
          feedsIntoMatchKey: `${match.feedsIntoRound}-${newParentMatchNumber}`
        }
      } else {
        // Regular match: renumber and recompute next-round reference
        const roundKey = `${match.round}-${match.matchNumber}`
        const newMatchNumber = regularRenumberMap.get(roundKey) ?? match.matchNumber
        const next = computeNextMatch(match.round, newMatchNumber)

        // Update PQ refs with new PQ numbers
        let newPlayer1PQRef = match.player1PQRef
        if (match.player1PQRef) {
          const oldPQNum = parseInt(match.player1PQRef.replace('PQ-', ''))
          const newPQNum = pqRenumberMap.get(oldPQNum)
          if (newPQNum !== undefined) newPlayer1PQRef = `PQ-${newPQNum}`
        }
        let newPlayer2PQRef = match.player2PQRef
        if (match.player2PQRef) {
          const oldPQNum = parseInt(match.player2PQRef.replace('PQ-', ''))
          const newPQNum = pqRenumberMap.get(oldPQNum)
          if (newPQNum !== undefined) newPlayer2PQRef = `PQ-${newPQNum}`
        }

        return {
          ...match,
          matchNumber: newMatchNumber,
          nextRound: next.nextRound,
          nextMatchNumber: next.nextMatchNumber,
          player1PQRef: newPlayer1PQRef,
          player2PQRef: newPlayer2PQRef
        }
      }
    })
  }

  // Add a new regular match
  const handleAddMatch = () => {
    const round1Matches = matches.filter(m => m.round === 1)
    const matchNumber = round1Matches.length + 1
    const { nextRound, nextMatchNumber } = computeNextMatch(1, matchNumber)

    // Initialize player fields based on team size
    const isTeam = bracket?.divisionTeamSize > 1
    const emptyPlayer = isTeam ? [] : ''

    const matchId = Date.now()
    const newMatch = {
      id: matchId,
      round: 1,
      matchNumber,
      player1: emptyPlayer,
      player1Seed: '',
      player2: emptyPlayer,
      player2Seed: '',
      nextRound,
      nextMatchNumber
    }
    setMatches([...matches, newMatch])
    setNewMatchId(matchId)
    setShowValidationErrors(false)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const addBtn = document.querySelector('[data-add-match-button]')
        if (addBtn) {
          const rect = addBtn.getBoundingClientRect()
          const targetY = window.innerHeight * 0.65
          const scrollTarget = window.scrollY + rect.top - targetY
          window.scrollTo({ top: scrollTarget, behavior: 'smooth' })
        }
      })
    })
  }

  // Insert a new regular (round 1) match immediately after an existing one,
  // shifting downstream match numbers so all feeds-into references stay
  // consistent. This replaces the old workflow of deleting and re-adding every
  // trailing match when the admin forgot one in the middle.
  //
  // Keyboard nav note: tab-index derivations on match cards (e.g. the PQ
  // button's `parentMatchSeedTabIndex = 1000 + matchNumber*10 + slot`) are
  // computed from the current `matches` array on every render, so they
  // recompute naturally after the renumber. The auto-focus effect watches
  // `newMatchId` and re-reads the renumbered match, so the new card still
  // receives focus at its post-renumber position.
  const handleInsertMatch = (afterMatchId) => {
    const afterMatch = matches.find(m => m.id === afterMatchId)
    if (!afterMatch || afterMatch.round !== 1) return

    const isTeam = bracket?.divisionTeamSize > 1
    const emptyPlayer = isTeam ? [] : ''
    const matchId = Date.now()

    // Fractional matchNumber sorts between the reference match and its
    // successor; renumberAllMatches normalises to sequential integers.
    const tempNumber = afterMatch.matchNumber + 0.5

    const newMatch = {
      id: matchId,
      round: 1,
      matchNumber: tempNumber,
      player1: emptyPlayer,
      player1Seed: '',
      player2: emptyPlayer,
      player2Seed: '',
      nextRound: 2,
      nextMatchNumber: Math.ceil(tempNumber / 2),
    }

    // DUB-55: insert after the R1's PQ group (if any) so the new match
    // doesn't land visually between the R1 match and its pre-qualifiers.
    let insertAfterIndex = matches.findIndex(m => m.id === afterMatchId)
    while (
      insertAfterIndex + 1 < matches.length &&
      matches[insertAfterIndex + 1].round === 0 &&
      matches[insertAfterIndex + 1].feedsIntoMatchNumber === afterMatch.matchNumber
    ) {
      insertAfterIndex += 1
    }
    const withNew = [
      ...matches.slice(0, insertAfterIndex + 1),
      newMatch,
      ...matches.slice(insertAfterIndex + 1),
    ]

    setMatches(renumberAllMatches(withNew))
    setNewMatchId(matchId)
    setShowValidationErrors(false)

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-match-id="${matchId}"]`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    })
  }

  // Add a PQ match for a specific player slot (1 or 2)
  const handleAddPQMatch = (parentMatchId, slot) => {
    const parentMatch = matches.find(m => m.id === parentMatchId)

    // Generate unique PQ match_number (independent of target match)
    const pqMatches = matches.filter(m => m.round === 0)
    const maxPQNumber = pqMatches.length > 0 ? Math.max(...pqMatches.map(m => m.matchNumber)) : 0
    const newPQMatchNumber = maxPQNumber + 1

    // Initialize player fields based on team size
    const isTeam = bracket?.divisionTeamSize > 1
    const emptyPlayer = isTeam ? [] : ''

    const pqMatchId = Date.now()
    const newPQMatch = {
      id: pqMatchId,
      round: 0, // PQ round
      matchNumber: newPQMatchNumber,
      player1: emptyPlayer,
      player1Seed: '',
      player2: emptyPlayer,
      player2Seed: '',
      nextRound: parentMatch.round,
      nextMatchNumber: parentMatch.matchNumber,
      feedsIntoMatchKey: `${parentMatch.round}-${parentMatch.matchNumber}`,
      feedsIntoRound: parentMatch.round,
      feedsIntoMatchNumber: parentMatch.matchNumber,
      feedsIntoSlot: slot
    }

    // Update parent match to reference this PQ match for the specified slot
    const pqRefKey = slot === 1 ? 'player1PQRef' : 'player2PQRef'
    const playerKey = slot === 1 ? 'player1' : 'player2'
    const seedKey = slot === 1 ? 'player1Seed' : 'player2Seed'

    const updatedMatches = matches.map(m =>
      m.id === parentMatchId
        ? { ...m, [pqRefKey]: `PQ-${newPQMatchNumber}`, [playerKey]: emptyPlayer, [seedKey]: '' }
        : m
    )

    // Find the correct index to insert the PQ match
    // PQ for slot 1 should always come before PQ for slot 2
    const parentIndex = updatedMatches.findIndex(m => m.id === parentMatchId)
    const newMatches = [...updatedMatches]

    let insertIndex = parentIndex + 1

    if (slot === 2) {
      // If adding slot 2 PQ, check if there's already a slot 1 PQ after the parent
      // If so, insert after it
      const existingSlot1PQ = updatedMatches.find((m, idx) =>
        idx > parentIndex &&
        m.round === 0 &&
        m.feedsIntoRound === parentMatch.round &&
        m.feedsIntoMatchNumber === parentMatch.matchNumber &&
        m.feedsIntoSlot === 1
      )
      if (existingSlot1PQ) {
        insertIndex = updatedMatches.findIndex(m => m.id === existingSlot1PQ.id) + 1
      }
    } else if (slot === 1) {
      // If adding slot 1 PQ, check if there's already a slot 2 PQ after the parent
      // If so, insert before it (right after parent)
      // insertIndex is already parentIndex + 1, which is correct
    }

    newMatches.splice(insertIndex, 0, newPQMatch)

    // Renumber so the new PQ slots into the correct position relative to its
    // parent match's bracket position (rather than landing at the tail with
    // matchNumber = maxPQ+1). Also rewrites parent matches' playerXPQRef.
    setMatches(renumberAllMatches(newMatches))
    setNewMatchId(pqMatchId) // Set the new PQ match ID to trigger auto-focus
  }

  // Delete match and any associated PQ matches
  const handleDeleteMatch = (matchId) => {
    const matchToDelete = matches.find(m => m.id === matchId)
    if (!matchToDelete) return

    const matchesToRemove = [matchId]

    // If this match has PQ matches feeding into it, delete those too
    if (matchToDelete.player1PQRef || matchToDelete.player2PQRef) {
      // Find PQ matches that feed into this match
      const pqMatches = matches.filter(m =>
        m.round === 0 &&
        m.feedsIntoRound === matchToDelete.round &&
        m.feedsIntoMatchNumber === matchToDelete.matchNumber
      )
      pqMatches.forEach(pq => matchesToRemove.push(pq.id))
    }

    // If deleting a PQ match, remove the PQ reference from parent match
    if (matchToDelete.round === 0) {
      const parentMatch = matches.find(m =>
        m.round === matchToDelete.feedsIntoRound &&
        m.matchNumber === matchToDelete.feedsIntoMatchNumber
      )
      if (parentMatch) {
        // Determine which slot this PQ feeds and clear the appropriate reference
        const slot = matchToDelete.feedsIntoSlot
        const pqRefKey = slot === 1 ? 'player1PQRef' : 'player2PQRef'

        const afterDelete = matches.map(m =>
          m.id === parentMatch.id
            ? { ...m, [pqRefKey]: undefined }
            : m
        ).filter(m => !matchesToRemove.includes(m.id))
        setMatches(renumberAllMatches(afterDelete))
        return
      }
    }

    // Remove all matches in the list and renumber to fill gaps
    const afterDelete = matches.filter(m => !matchesToRemove.includes(m.id))
    setMatches(renumberAllMatches(afterDelete))
  }

  // Replace all matches with a freshly-built list. Used by the Quick Setup
  // view, which generates a full round-1 bracket from a seeded roster in one
  // go. Clears validation error display so the fresh matches show errors only
  // as fields are touched (matches handleAddMatch behavior).
  const handleReplaceMatches = (newMatches) => {
    setMatches(renumberAllMatches(newMatches))
    setShowValidationErrors(false)
    setTouchedFields(new Set())
  }

  // Update match data
  const handleUpdateMatch = (matchId, field, value) => {
    setMatches(prevMatches => prevMatches.map(m => {
      if (m.id === matchId) {
        return { ...m, [field]: value }
      }
      return m
    }))
  }

  // Handle team member changes
  const handlePlayer1TeamChange = (matchId, index, value) => {
    setMatches(matches.map(m => {
      if (m.id === matchId) {
        const team = Array.isArray(m.player1) ? [...m.player1] : []
        team[index] = value
        return { ...m, player1: team }
      }
      return m
    }))
  }

  const handlePlayer2TeamChange = (matchId, index, value) => {
    setMatches(matches.map(m => {
      if (m.id === matchId) {
        const team = Array.isArray(m.player2) ? [...m.player2] : []
        team[index] = value
        return { ...m, player2: team }
      }
      return m
    }))
  }

  // Save to database.
  // When `silent: true`, the validation-errors confirm prompt is bypassed and
  // we just save what we have. Used by the back-breadcrumb auto-save (admin
  // shouldn't lose work if they navigate away with seeds still in progress).
  // Returns true on a successful save, false on failure or user-cancelled
  // validation prompt.
  const handleSave = async ({ silent = false } = {}) => {
    // Remember which element had focus so we can restore it after save
    const focusedEl = document.activeElement
    const focusKey = focusedEl?.getAttribute('data-validation-key') || focusedEl?.getAttribute('data-match-key')

    // Check if there are any validation errors
    if (!silent && Object.keys(validationErrors).length > 0) {
      // Show validation errors
      setShowValidationErrors(true)

      // Build specific error message based on actual errors
      const errorTypes = new Set()
      Object.entries(validationErrors).forEach(([key, errorMsg]) => {
        if (key.includes('-seed')) {
          // Seed field errors
          if (errorMsg.includes('Seed required')) errorTypes.add('Empty seed fields')
          if (errorMsg.includes('Duplicate seed')) errorTypes.add('Duplicate seed numbers')
          if (errorMsg.includes('Max 999')) errorTypes.add('Seed numbers exceeding 999')
          if (errorMsg.includes('Must be 1-999')) errorTypes.add('Invalid seed numbers (must be 1-999)')
        } else if (key.includes('-player')) {
          // Player field errors
          if (errorMsg.includes('Player required')) errorTypes.add('Empty player fields')
        }
      })

      // Show confirm dialog with error details - allow user to save anyway
      const confirmed = await notify.confirm({
        title: 'Validation Errors',
        description: 'There are validation issues with your bracket',
        details: Array.from(errorTypes),
        confirmText: 'Save Anyway',
        cancelText: 'Go Back',
        variant: 'warning'
      })

      if (!confirmed) {
        return false
      }
    }

    try {
      setSaving(true)

      const isTeam = bracket?.divisionTeamSize > 1

      // Collect all unique teams/players from matches, keyed by position
      const playersMap = new Map()

      matches.forEach(match => {
        // Build player entry with position data for each slot
        const buildEntry = (player, seedValue, slot) => {
          const seed = parseInt(seedValue) || 0
          if (seed <= 0) return null

          let playerIds
          if (isTeam && Array.isArray(player)) {
            playerIds = player.filter(p => p?.id && p.id !== 'TBD').map(p => p.id)
          } else if (player?.id && player.id !== 'TBD') {
            playerIds = [player.id]
          } else if (player?.id === 'TBD') {
            playerIds = []
          } else {
            return null
          }

          return {
            player_ids: playerIds,
            seed,
            round: match.round,
            match_number: match.matchNumber,
            slot
          }
        }

        const p1 = buildEntry(match.player1, match.player1Seed, 1)
        if (p1) playersMap.set(`${match.id}-p1`, p1)

        const p2 = buildEntry(match.player2, match.player2Seed, 2)
        if (p2) playersMap.set(`${match.id}-p2`, p2)
      })

      const playersData = Array.from(playersMap.values())

      // Transform matches for database - team references resolved by position in RPC
      const matchesData = matches.map(match => ({
        round: match.round,
        match_number: match.matchNumber,
        feeds_into_round: match.feedsIntoRound || null,
        feeds_into_match_number: match.feedsIntoMatchNumber || null,
        feeds_into_slot: match.feedsIntoSlot || null
      }))

      // Detect deleted matches by position — find original DB positions not in current matches.
      // ID-based detection fails when matches are renumbered after a middle deletion:
      // the renumbered matches keep their UUIDs but shift positions, leaving orphan
      // rows at the old tail positions that the upsert never touches.
      const currentMatchPositions = new Set(matchesData.map(m => `${m.round}-${m.match_number}`))
      const deletedMatchIds = originalMatches
        .filter(m => !currentMatchPositions.has(`${m.round}-${m.matchNumber}`))
        .map(m => m.id)

      // Detect deleted teams by position key (round-match_number-slot)
      const currentPositionKeys = new Set(
        playersData.map(p => `${p.round}-${p.match_number}-${p.slot}`)
      )
      const deletedBracketPlayerIds = originalPlayers
        .filter(team => !currentPositionKeys.has(`${team.round}-${team.match_number}-${team.slot}`))
        .map(team => team.id)

      // Delete removed matches
      if (deletedMatchIds.length > 0) {
        await deleteSeeds(deletedMatchIds)
      }

      // Delete removed teams
      if (deletedBracketPlayerIds.length > 0) {
        await deleteBracketTeams(deletedBracketPlayerIds)
      }

      // Call save function for upserts
      await saveBracketData(bracketId, playersData, matchesData)

      // Only clear validation error display if there were no errors
      // If there were errors and user saved anyway, keep them visible
      if (Object.keys(validationErrors).length === 0) {
        setShowValidationErrors(false)
        setTouchedFields(new Set())
      }

      // Show success notification
      notify.success('Bracket saved successfully!')

      // Quiet refresh: fetch fresh data to update IDs and deletion tracking
      // without showing a loading spinner or resetting scroll position
      try {
        const freshData = await fetchBracketWithDetails(bracketId)
        if (freshData) {
          const seeds = freshData.seeds || []

          // Build position -> seed ID map for updating match IDs
          const seedByPosition = new Map()
          seeds.forEach(seed => {
            seedByPosition.set(`${seed.round}-${seed.match_number}`, seed.id)
          })

          // Build fresh team map for updating originalPlayers
          const freshTeamMap = new Map()
          seeds.forEach(seed => {
            if (seed.team_one) freshTeamMap.set(seed.team_one.id, seed.team_one)
            if (seed.team_two) freshTeamMap.set(seed.team_two.id, seed.team_two)
          })

          // Update match IDs in place (swap temp numeric IDs for real UUIDs)
          setMatches(prev => prev.map(match => {
            const dbId = seedByPosition.get(`${match.round}-${match.matchNumber}`)
            return dbId ? { ...match, id: dbId } : match
          }))

          // Update deletion tracking baselines. Exclude materialized
          // advancement rounds (round >= 2 on single_elim) so a later save's
          // position-based deletion detection never treats those DB-owned
          // skeleton rows as removed matches — they aren't loaded into `matches`.
          const refreshIsSingleElim = (bracket?.format || 'single_elim') === 'single_elim'
          setOriginalMatches(
            seeds
              .filter(s => typeof s.id === 'string' && (!refreshIsSingleElim || s.round <= 1))
              .map(s => ({
                id: s.id,
                round: s.round,
                matchNumber: s.match_number
              }))
          )
          setOriginalPlayers(Array.from(freshTeamMap.values()))
        }
      } catch (refreshErr) {
        console.error('Error refreshing bracket data after save:', refreshErr)
      }
      return true
    } catch (err) {
      console.error('Error saving bracket:', err)
      notify.error(`Error saving bracket: ${err.message}`)
      return false
    } finally {
      setSaving(false)
      // Restore focus to the element that was focused before save
      if (focusKey) {
        requestAnimationFrame(() => {
          const target = document.querySelector(`[data-validation-key="${focusKey}"]`) ||
                         document.querySelector(`[data-match-key="${focusKey}"]`)
          target?.focus()
        })
      }
    }
  }

  // Update ref so keyboard shortcut can call handleSave
  saveRef.current = handleSave

  return {
    // State
    bracket,
    tournament,
    tournamentBrackets,
    loading,
    saving,
    matches,
    isHeaderSticky,
    displayValidationErrors,

    // Refs
    matchRefs,
    stickyHeaderRef,

    // Handlers
    handleAddMatch,
    handleInsertMatch,
    handleAddPQMatch,
    handleDeleteMatch,
    handleUpdateMatch,
    handleReplaceMatches,
    handlePlayer1TeamChange,
    handlePlayer2TeamChange,
    handleSave,
    handleFocusCapture,
    handleBlurCapture,
    getUsedPlayerIds,
  }
}
